import React, { useState } from 'react';
import USAMap from 'react-usa-map';
import { useLanguage } from '../context/LanguageContext';
import { Shield, Phone } from 'lucide-react';
import { companyInfo } from '../mock';
import { Card, CardContent } from './ui/card';

// Full state names mapping
const stateNames = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia'
};

const ServiceStates = () => {
  const { language } = useLanguage();
  const [hoveredState, setHoveredState] = useState(null);
  const [hoveredPR, setHoveredPR] = useState(false);

  const content = {
    en: {
      title: "Where We Are Licensed",
      subtitle: "Ask us about availability in your state",
      description: "We are working to serve clients across multiple states. Contact us to confirm current licensing status and product availability in your jurisdiction.",
      legend: {
        askAbout: "Ask About Availability"
      },
      noDataYet: "License verification in progress. Please contact us to confirm our current licensing status in your state.",
      contactPrompt: "Questions about licensing or availability?",
      callUs: "Call us at",
      note: "Note: Licensing status is distinct from product and carrier availability, which may vary by state and insurance company.",
      clickForInfo: "Hover over any state for details",
      puertoRico: "Puerto Rico"
    },
    es: {
      title: "Dónde Estamos Licenciados",
      subtitle: "Consúltanos sobre la disponibilidad en tu estado",
      description: "Estamos trabajando para servir a clientes en múltiples estados. Contáctenos para confirmar el estado actual de licencias y disponibilidad de productos en su jurisdicción.",
      legend: {
        askAbout: "Consulte Disponibilidad"
      },
      noDataYet: "Verificación de licencias en progreso. Por favor contáctenos para confirmar nuestro estado de licencias actual en su estado.",
      contactPrompt: "¿Preguntas sobre licencias o disponibilidad?",
      callUs: "Llámenos al",
      note: "Nota: El estado de licencias es distinto de la disponibilidad de productos y aseguradoras, que puede variar según el estado y la compañía de seguros.",
      clickForInfo: "Pase el cursor sobre cualquier estado para más detalles",
      puertoRico: "Puerto Rico"
    }
  };

  const text = content[language];

  // Handler for state hover
  const mapHandler = (event) => {
    const stateAbbr = event.target.dataset.name;
    setHoveredState(stateAbbr);
  };

  // Handler for state mouse leave
  const mapLeaveHandler = () => {
    setHoveredState(null);
  };

  // Custom styling - all states are grey (ask about availability)
  const statesCustomConfig = () => {
    const config = {};
    Object.keys(stateNames).forEach(state => {
      config[state] = {
        fill: '#d1d5db', // Gray for "Ask About Availability"
        clickHandler: () => {}
      };
    });
    return config;
  };

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-block bg-blue-100 p-3 rounded-full mb-4">
            <Shield className="w-8 h-8 text-blue-700" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {text.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-2">
            {text.subtitle}
          </p>
          <p className="text-base text-gray-500 max-w-2xl mx-auto">
            {text.description}
          </p>
        </div>

        {/* Legend */}
        <div className="flex justify-center gap-6 mb-8">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gray-300 rounded"></div>
            <span className="text-sm text-gray-700">{text.legend.askAbout}</span>
          </div>
        </div>

        {/* Map Container */}
        <div className="max-w-5xl mx-auto mb-8">
          <Card className="shadow-xl border-none">
            <CardContent className="p-4 md:p-8">
              <div className="relative">
                <div 
                  onMouseEnter={mapHandler} 
                  onMouseLeave={mapLeaveHandler}
                  className="w-full"
                  style={{
                    '--hover-fill': '#9ca3af'
                  }}
                >
                  <style dangerouslySetInnerHTML={{__html: `
                    .state path {
                      stroke: #ffffff;
                      stroke-width: 1.5;
                      cursor: pointer;
                      transition: fill 0.2s;
                    }
                    .state path:hover {
                      fill: #9ca3af !important;
                    }
                  `}} />
                  <USAMap 
                    customize={statesCustomConfig()} 
                    width="100%"
                    height="auto"
                  />
                </div>

                {/* Puerto Rico Inset */}
                <div className="mt-6 flex justify-center">
                  <div 
                    className="relative inline-block"
                    onMouseEnter={() => setHoveredPR(true)}
                    onMouseLeave={() => setHoveredPR(false)}
                  >
                    <svg width="120" height="60" viewBox="0 0 120 60" className="border-2 border-white rounded shadow-sm">
                      <rect
                        x="10"
                        y="15"
                        width="100"
                        height="30"
                        fill={hoveredPR ? '#9ca3af' : '#d1d5db'}
                        stroke="#ffffff"
                        strokeWidth="2"
                        className="cursor-pointer transition-colors duration-200"
                        rx="2"
                      />
                      <text 
                        x="60" 
                        y="35" 
                        fontSize="14" 
                        fill="#374151" 
                        fontWeight="600" 
                        textAnchor="middle"
                        dominantBaseline="middle"
                        pointerEvents="none"
                      >
                        PR
                      </text>
                    </svg>
                    <p className="text-xs text-gray-600 text-center mt-1">{text.puertoRico}</p>
                  </div>
                </div>
                
                {/* State Info Display */}
                {(hoveredState || hoveredPR) && (
                  <div className="text-center mt-6 p-4 bg-blue-50 rounded-lg">
                    <p className="font-semibold text-gray-900 text-lg">
                      {hoveredPR ? text.puertoRico : stateNames[hoveredState]}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">{text.legend.askAbout}</p>
                  </div>
                )}

                <p className="text-center text-sm text-gray-500 mt-6">
                  {text.clickForInfo}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* No Data Message */}
        <div className="max-w-3xl mx-auto mb-8">
          <Card className="border-l-4 border-blue-500">
            <CardContent className="p-6">
              <p className="text-gray-700 mb-4">
                {text.noDataYet}
              </p>
              <p className="text-sm text-gray-600 italic">
                {text.note}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Contact CTA */}
        <div className="text-center">
          <p className="text-lg text-gray-700 mb-4">{text.contactPrompt}</p>
          <a
            href={`tel:${companyInfo.phone}`}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-full text-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg"
          >
            <Phone className="w-5 h-5" />
            <span>{text.callUs} {companyInfo.phone}</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default ServiceStates;
